/* =========================================================================
   TelemetryTimeframeDropdown.tsx — Timeframe selector dropdown
   Matches the Telemetry Viz Select / FloatingMenu design pattern exactly.
   ========================================================================= */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { TIMEFRAME_OPTIONS, getTimeframeLabel, getFilteredTimeframeOptions } from '../utils/telemetry-time-range.utils';
import CustomToolTip from '../../CustomToolTip';
import '../styles/telemetry-timeframe-dropdown.css';

export interface TelemetryTimeframeDropdownProps {
  timeframeMinutes: number;
  onSelectTimeframe: (minutes: number) => void;
  simulationDurationMinutes?: number | null;
  className?: string;
}

export const TelemetryTimeframeDropdown: React.FC<TelemetryTimeframeDropdownProps> = ({
  timeframeMinutes,
  onSelectTimeframe,
  simulationDurationMinutes,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  // Filter available timeframe options based on simulation duration with enclosing bucket support
  const finalOptions = useMemo(
    () => getFilteredTimeframeOptions(simulationDurationMinutes),
    [simulationDurationMinutes]
  );

  const activeLabel = getTimeframeLabel(timeframeMinutes);

  const handleSelect = (minutes: number) => {
    setIsOpen(false);
    if (minutes === timeframeMinutes) {
      return; // Duplicate selection guard: no-op
    }
    onSelectTimeframe(minutes);
  };

  return (
    <div ref={containerRef} className={`td-tf-dropdown-wrap ${className}`}>
      <CustomToolTip title="Timeframe">
        <button
          type="button"
          className={`td-tf-viz-button ${isOpen ? 'td-tf-viz-button--open' : ''}`}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="td-tf-btn-label">{activeLabel}</span>
          <svg
            className={`td-tf-btn-arrow ${isOpen ? 'td-tf-btn-arrow--open' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path d="M4 6l4 4 4-4" stroke="#7B7B7B" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </CustomToolTip>

      {isOpen && (
        <div className="td-tf-floating-menu" role="listbox">
          <div className="td-tf-menu-top">
            <div className="td-tf-menu-title">TIMEFRAME</div>
          </div>
          <div className="td-tf-menu-list">
            {finalOptions.map((opt) => {
              const isSelected = opt.minutes === timeframeMinutes;
              return (
                <button
                  key={opt.minutes}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`td-tf-menu-item ${isSelected ? 'td-tf-menu-item--active' : ''}`}
                  onClick={() => handleSelect(opt.minutes)}
                >
                  <span className="td-tf-menu-item-left">
                    <span className="td-tf-menu-item-label">{opt.label}</span>
                  </span>
                  {isSelected && <span className="td-tf-menu-check">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TelemetryTimeframeDropdown;
